import { bookingService } from '@/services/bookingService';
import { ContactInfoData, IPromotion } from '@/types/interface';
import { AdditionServiceList, Tour, TourAdditionalService, TourDetail, TourPricing, VisitPlaceList } from '@/types/tourBooking';
import { convertImages } from '@/utils/utils';
import moment, { Moment } from 'moment';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next13-progressbar';
import useStoreApp from '@/store';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

export enum PaymentMethodEnum {
  PAYPAL = 'PAYPAL',
  VNPAY = 'VNPAY',
  PAY_LATER = 'PAY_LATER',
  MBBANK = 'MBBANK',
  VNPAYQR = 'VNPAYQR',
  VNBANK = 'VNBANK',
  INTCARD = 'INTCARD',
  APPLEPAY = 'APPLEPAY',
  SAMSUNGPAY = 'SAMSUNGPAY',
  GOOGLEPAY = 'GOOGLEPAY',
  VIETQRPAYGLOBAL = 'VIETQRPAYGLOBAL',
  VTBBANKQR = 'VTBBANKQR',
  ONEPAY = 'ONEPAY'
}

export type PaymentMethod = keyof typeof PaymentMethodEnum;

interface TourBookingContextType {
  selectedTourIdForDetail: string | null;
  setSelectedTourIdForDetail: (id: string | null) => void;
  selectedTourIdForBooking: string | null;
  setSelectedTourIdForBooking: (id: string | null) => void;
  bookingTourData: TourBookingData;
  setBookingTourData: React.Dispatch<React.SetStateAction<TourBookingData>>;
  tourDetail: TourDetail | null;
  contactInfo: ContactInfoData;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfoData>>;
  durationOptions: { id: string, label: string; value: number }[];
  services: TourAdditionalService[];
  locale?: string;
  // tourPrice: number;
  // servicesPrice: number;
  // vatPrice: number;
  // discount: number;
  // totalAmount: number;
  tourBookingDoneId: string | null;
  setTourBookingDoneId: (id: string | null) => void;
  selectedTour: Tour | null;
  tours?: Tour[];
  storePromotion: null | IPromotion;
  setStorePromotion: React.Dispatch<React.SetStateAction<null | IPromotion>>;
  computedPricing: IComputeTourPriceResponse | null;
  setComputedPricing: React.Dispatch<React.SetStateAction<IComputeTourPriceResponse | null>>;
  isFetchingTourDetail: boolean;
  isFetchingTours: boolean;
  showConfirmModal?: boolean;
  setShowConfirmModal?: React.Dispatch<React.SetStateAction<boolean>>;
  finishRenderPaypalButtons?: boolean;
  setFinishRenderPaypalButtons?: React.Dispatch<React.SetStateAction<boolean>>;
  toursStorage: Tour[];
}

interface TourBookingData {
  tourPriceOptionId: string;
  date: Moment;
  time: Moment;
  additionalServiceIds: string[];
  pickUp?: {
    lat: number;
    lng: number;
  };
  pickUpAddress: string;
  passengerNormal: number;
  passengerHalf?: number;
  note?: string;
  promotionDetailId?: string;
  promotionId?: string;
  collaboratorId?: string;
  isGoNow?: boolean;
  dialCode?: string;
}

export const TourBookingContext = createContext<
  TourBookingContextType | undefined
>(undefined);

interface TourBookingProviderProps {
  children: ReactNode;
  locale?: string;
  tours?: Tour[];
  tourSlug?: string;
  tourDetailServer?: TourDetail;
}

const initTourBookingData: TourBookingData = {
  tourPriceOptionId: '',
  date: moment(),
  time: moment().add(30, 'minutes'),
  additionalServiceIds: [],
  pickUp: undefined,
  pickUpAddress: '',
  passengerNormal: 0,
  passengerHalf: undefined,
  note: undefined,
  collaboratorId: undefined
};

const initContactInfoData: ContactInfoData = {
  fullName: '',
  phone: '',
  email: '',
  zalo: undefined,
  whatsapp: undefined,
  kakaotalk: undefined,
  viber: undefined
};

interface IComputeTourPriceResponse {
  additional_price: number;
  discount: number;
  discount_percent: number;
  discount_price: number;
  money?: number;
  price: number;
  visitPlaceTicketFee: number;
  pureTourPrice: number;
  tour_pricing?: TourPricing;
  additional_service_list: AdditionServiceList[];
  visit_place_list: VisitPlaceList[];
  additional_passenger_price: number;
}

export const convertTourDetailImages = (data: any): TourDetail => {
  return {
    ...data,
    images: JSON.parse(data.images) as string[], // Convert images to array
  };
};

export const TourBookingProvider: React.FC<TourBookingProviderProps> = ({
  children,
  locale,
  tours,
  tourSlug,
  tourDetailServer,
}) => {
  const router = useRouter();

  const { user } = useStoreApp(state => state.globalSlice);

  const { t } = useTranslation('error');

  const [toursStorage, setToursStorage] = useState<Tour[]>(tours ?? []);

  const [storePromotion, setStorePromotion] = useState<null | IPromotion>(null);

  const [selectedTourIdForDetail, setSelectedTourIdForDetail] = useState<
    string | null
  >(null);

  const [selectedTourIdForBooking, setSelectedTourIdForBooking] = useState<
    string | null
  >(null);

  const [tourDetail, setTourDetail] = useState<TourDetail | null>(tourDetailServer ?? null);

  const [bookingTourData, setBookingTourData] =
    useState<TourBookingData>(initTourBookingData);

  const [contactInfo, setContactInfo] =
    useState<ContactInfoData>(initContactInfoData);

  const [tourBookingDoneId, setTourBookingDoneId] = useState<string | null>(
    null,
  );

  const [selectedTour, setSelectedTour] = useState<TourDetail | null>(null);

  const [durationOptions, setDurationOptions] = useState<
    { id: string, label: string; value: number }[]
  >([]);
  const [services, setServices] = useState<TourAdditionalService[]>([]);

  const [computedPricing, setComputedPricing] = useState<IComputeTourPriceResponse | null>(null);

  const [isFetchingTourDetail, setIsFetchingTourDetail] = useState<boolean>(false);
  const [isFetchingTours, setIsFetchingTours] = useState<boolean>(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [finishRenderPaypalButtons, setFinishRenderPaypalButtons] = useState(false);

  const API_URL = process.env.NEXT_PUBLIC_BASE_URL;

  const [currentDisplayLang, setCurrentDisplayLang] = useState(locale);

  useEffect(() => {

    if (isFetchingTours || tourDetailServer) {
      return;
    }

    const _getListTours = async () => {
      try {
        const list_tour_url = `${API_URL}booking_tour/${locale}/list_tour`;
        const res = await fetch(list_tour_url);

        if (!res.ok) {
          throw new Error(`Failed to fetch, status code: ${res.status}`);
        }

        const data = await res.json();
        const fetchedTours = convertImages(data);
        setToursStorage(fetchedTours);
        return fetchedTours;
      } catch (error) {
        console.error('Error fetching tours:', error);
        return [];
      }
    }

    const _getSelectTour = async (tour: Tour[]) => {
      if (selectedTourIdForBooking || tourSlug) {
        let foundTour: Tour | undefined = undefined;

        if (selectedTourIdForBooking) {
          foundTour = tour.find(t => t.id === selectedTourIdForBooking);
        }
        else if (tourSlug) {
          foundTour = tour.find(t => t.slug === tourSlug);

          if (!foundTour) {
            router.push('./_error');
            return;
          }
        }

        if (foundTour) {
          fetchTourDetail();
          //_updateDataTour(foundTour as TourDetail);
        }
        else if (user) {
          _clearDataTour();
          setTimeout(() => {
            toast.error(t('error.404.slug_error'));
          }, 500);
        }
        else {
          fetchTourDetail();
        }
      }
      else if (tour.length > 0) {
        _updateDataTour(tour.at(0) as TourDetail);
      }
    }

    if (user) {
      if (currentDisplayLang !== locale || (!tours && toursStorage.length === 0)) {
        setIsFetchingTours(true);

        setCurrentDisplayLang(locale);

        _getListTours().then(fetchedTours => {
          setIsFetchingTours(false);
          _getSelectTour(fetchedTours);
        });
      }
      else {
        _getSelectTour(tours ?? toursStorage);
      }
    }
    else if (tourDetailServer) {
      _updateDataTour(tourDetailServer);
    }
    else {
      setIsFetchingTours(true);

      if (isFetchingTourDetail) { return; }

      setIsFetchingTourDetail(true);

      setTimeout(() => {
        fetchTourDetail();
      }, 400);
    }


    return () => {
      setSelectedTour(null);
      setDurationOptions([]);
    }

  }, [locale, selectedTourIdForBooking]);

  const _clearDataTour = () => {
    setSelectedTour(null);
    setDurationOptions([]);
    setSelectedTourIdForBooking(null);
    setTourDetail(null);
  }

  const _updateDataTour = (tour: TourDetail) => {
    setSelectedTour(tour);
    setTourDetail(tour);
    updateDurationOptions(tour);

    if (tour.tourServices) {
      setServices(tour.tourServices);
    }
    setComputedPricing(null);
  }

  function updateDurationOptions(tour?: TourDetail) {
    if (!tour) {
      setDurationOptions([]);;
      return
    }
    const formattedOptions = tour.tourPriceOptions?.map(option => {

      const unit = locale === 'vi' ? 'phút' : locale === 'en' ? 'minutes' : '분';
      const durationStr = `${option.duration} ${unit}`;
      const title = option.contents?.[0]?.title;
      const contentTitle = title ?? durationStr; // title ? `${title} [${durationStr}]` : durationStr;

      return ({
        id: option.id,
        label: contentTitle,
        value: option.duration,
      });
    });
    setDurationOptions(formattedOptions);

    if (!selectedTourIdForBooking || selectedTourIdForBooking !== tour.id) {
      setSelectedTourIdForBooking(tour.id);

      if (computedPricing) {
        setComputedPricing(null);
      }
    }

    if (tourDetail && tour.id !== tourDetail.id) {
      setTourDetail(null);
    }
  }

  const fetchTourDetail = async () => {
    //console.log(" Tour Booking Provider -> fetchTourDetail -> selectedTourIdForBooking: ", selectedTourIdForBooking);

    if (locale) {
      let fetchDetailUrl = '';

      if (selectedTourIdForBooking) {
        fetchDetailUrl = `${API_URL}booking_tour/${locale}/tour/${selectedTourIdForBooking}`
      }
      else if (tourSlug) {
        fetchDetailUrl = `${API_URL}booking_tour/${locale}/tour-slug/${tourSlug}`;
      }

      if (!fetchDetailUrl) {
        // throw new Error('Invalid Tour Detail');
        return;
      }

      fetch(fetchDetailUrl).then(async res => {

        const data = await res.json();
        try {
          const tour = convertTourDetailImages(data);

          if (tour.tourContents.length == 0) {
            _clearDataTour();

            if (user) {
              setTimeout(() => {
                toast.error(t('error.404.slug_error'));
              }, 500);
            }
            else {
              router.push('./_error');
            }
            return;
          }

          setTourDetail(tour);
          _updateDataTour(tour);

        }
        catch (error) {
          console.error('Error parsing tour detail:', error);
          if (res.status === 500) {
            setTourDetail(null);
            setServices([]);
            setSelectedTour(null);
            setDurationOptions([]);
          }
        }
        return res;
      })
        .catch(err => {
          console.error('Error parsing tour detail:', err);
          setTourDetail(null);
          setServices([]);
          setSelectedTour(null);
          setDurationOptions([]);
        })
        .finally(() => {
          setIsFetchingTourDetail(false);
          setIsFetchingTours(false);
        });
    }
  };

  useEffect(() => {
    if (user) {
      return;
    }

    if (tourDetailServer && tourDetail && tourDetail.id === tourDetailServer.id) {
      _updateDataTour(tourDetailServer);
      return;
    }

    return () => {
      _clearDataTour();
    }

  }, [locale, tourDetailServer]);

  //useEffect(() => {
  //  if (isFetchingTours || isFetchingTourDetail || tourDetail || !selectedTour) {
  //    return;
  //  }

  //  setIsFetchingTourDetail(true);

  //  setTimeout(() => {
  //    fetchTourDetail();
  //  }, 400);

  //  return () => {
  //    setTourDetail(null);
  //    setServices([]);
  //  }

  //}, [API_URL, locale, selectedTour]);

  //useEffect(() => {
  //  const fetchTourAdditionalServices = async () => {
  //    if (!locale) return;
  //    if (selectedTour === null) return;

  //    try {
  //      const res = await fetch(
  //        `${API_URL}booking_tour/${locale}/additional_service?tourType=${selectedTour.tourType}`,
  //      );
  //      const data: TourAdditionalService[] = await res.json();
  //      setServices(data);
  //    } catch (error) {
  //      console.error('Error fetching tour additional services:', error);
  //    }
  //  };

  //  fetchTourAdditionalServices();
  //}, [API_URL, locale, selectedTour]);

  const computePrice = useCallback(async () => {
    if (!selectedTour || bookingTourData.passengerNormal <= 0) {
      return;
    }
    if (computedPricing && !bookingTourData.tourPriceOptionId) {
      return;
    }

    const payload = {
      "tourId": selectedTour.id,
      "tourPriceOptionId": bookingTourData.tourPriceOptionId,
      "additionalServiceIds": bookingTourData.additionalServiceIds,
      "passengerNormal": bookingTourData.passengerNormal,
      "passengerHalf": 0,
      "collaboratorId": bookingTourData.collaboratorId,
      "promotionId": storePromotion?.promotionId
    };

    bookingService.computeTourPrice(payload).then(res => {
      const { data }: { data: IComputeTourPriceResponse } = res;
      setComputedPricing(data);
    })
      .catch(err => {
        console.error("err", err);
      });


  }
    , [
      //selectedTour,
      bookingTourData.collaboratorId,
      bookingTourData.tourPriceOptionId,
      storePromotion,
      bookingTourData.passengerNormal,
      bookingTourData.additionalServiceIds
    ]
  );

  useEffect(() => {
    computePrice();
  }, [computePrice]);

  // const tourPrice = useMemo(() => {

  //   const foundPriceOptions = selectedTour?.tourPriceOptions.find(
  //     t => t.duration === bookingTourData.duration,
  //   );
  //   if (foundPriceOptions) {
  //     const adultsPrice =
  //       bookingTourData.passengerNormal * foundPriceOptions.pricePassenger;
  //     const childrenPrice =
  //       ((bookingTourData.passengerHalf ?? 0) *
  //         foundPriceOptions.pricePassenger) /
  //       2;
  //     if (adultsPrice + childrenPrice <= foundPriceOptions.minPrice) {
  //       return foundPriceOptions.minPrice;
  //     }
  //     return adultsPrice + childrenPrice;
  //   }
  //   return 0;
  // }, [bookingTourData, selectedTour]);

  // const servicesPrice = useMemo(() => {
  //   const serviceIds = bookingTourData.additionalServiceIds;
  //   let tempPrice = 0;
  //   services.forEach(s => {
  //     if (serviceIds.includes(s.id)) {
  //       tempPrice = tempPrice + s.price;
  //     }
  //   });

  //   return tempPrice;
  // }, [bookingTourData.additionalServiceIds, services]);

  // const vatPrice = useMemo(
  //   () => (tourPrice + servicesPrice) / 10,
  //   [servicesPrice, tourPrice],
  // );

  // const totalAmount = useMemo(
  //   () => tourPrice + servicesPrice,
  //   [servicesPrice, tourPrice],
  // );

  return (
    <TourBookingContext.Provider
      value={{
        selectedTourIdForDetail,
        setSelectedTourIdForDetail,
        selectedTourIdForBooking,
        setSelectedTourIdForBooking,
        bookingTourData,
        setBookingTourData,
        tourDetail,
        contactInfo,
        setContactInfo,
        durationOptions,
        services,
        locale,
        // tourPrice,
        // servicesPrice,
        // vatPrice,
        // totalAmount,
        tourBookingDoneId,
        setTourBookingDoneId,
        selectedTour,
        tours,
        storePromotion,
        setStorePromotion,
        computedPricing,
        setComputedPricing,
        isFetchingTourDetail,
        isFetchingTours,
        showConfirmModal,
        setShowConfirmModal,
        finishRenderPaypalButtons,
        setFinishRenderPaypalButtons,
        toursStorage,
      }}
    >
      {children}
    </TourBookingContext.Provider>
  );
};
